import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  GlyphIcon,
  Modal,
  PrimaryActionButton,
  ResourceIcon,
  SecondaryActionButton,
  StatePanel,
  StonePanel,
} from '@aztec-realms/ui';

import {
  type AssistanceCategory,
  type AssistanceCommand,
  type AssistanceRequest,
  useAssistanceCommand,
  useTribeAssistance,
} from './data/tribe-assistance-api';
import { commandId } from './data/use-vertical-slice';
import { useNetworkStatus } from './network-status';
import { TribeSocialPanel } from './TribeSocialPanel';
import './tribe-assistance.css';

const categories: Record<AssistanceCategory, string> = {
  maize: 'Maize',
  stone: 'Stone',
  timber: 'Timber',
  wayfinder: 'Wayfinder',
  scribe: 'Scribe',
  builder: 'Builder',
};
const statuses: Record<AssistanceRequest['status'], string> = {
  open: 'Help wanted',
  fulfilled: 'Shared goal reached',
  cancelled: 'Request cancelled',
  expired: 'Request ended',
  closed: 'Request closed',
};
const confirmations = {
  spam: 'Spam or repeated requests',
  harassment: 'Harassment',
  unsafe: 'Unsafe content',
} satisfies Record<Extract<AssistanceCommand, { type: 'report' }>['reason'], string>;

function CategoryArt({ category }: { category: AssistanceCategory }) {
  return category === 'maize' || category === 'stone' || category === 'timber' ? (
    <ResourceIcon resource={category} className="tribe-assistance__art" />
  ) : (
    <GlyphIcon
      surface={category === 'wayfinder' ? 'explore' : category === 'scribe' ? 'decode' : 'home'}
      className="tribe-assistance__art"
    />
  );
}

interface Props {
  tribeId: string;
  enabled: boolean;
  locked: boolean;
  safetyLocked: boolean;
  onPlay: (request: AssistanceRequest) => void;
}

/** Key local drafts and ambiguous-command receipts by the current Tribe. */
export function TribeAssistancePanel(props: Props) {
  return <AssistancePanelContent key={`${props.tribeId}:${props.enabled}`} {...props} />;
}

function AssistancePanelContent({ tribeId, enabled, locked, safetyLocked, onPlay }: Props) {
  const [open, setOpen] = useState(false);
  const online = useNetworkStatus();
  const board = useTribeAssistance(tribeId, open && enabled && online);
  const command = useAssistanceCommand(tribeId);
  const pending = useRef<AssistanceCommand | null>(null);
  const inFlight = useRef(false);
  const [notice, setNotice] = useState('');
  const [selection, setSelection] = useState('');
  const [composerExpanded, setComposerExpanded] = useState<boolean>();
  const [reporting, setReporting] = useState<string>();
  const [reason, setReason] =
    useState<Extract<AssistanceCommand, { type: 'report' }>['reason']>('spam');
  const [blocking, setBlocking] = useState<{ memberId: string; name: string }>();
  const [withdrawing, setWithdrawing] = useState<string>();
  // Failed/new membership checks must never leave cached member actions visible.
  const data = enabled && !board.error && board.isFetchedAfterMount ? board.data : undefined;
  const available = Boolean(data?.enabled) && !board.isFetching;
  const disabled =
    !available || locked || safetyLocked || !online || command.isPending || Boolean(command.error);
  const choices = (data?.targets ?? []).flatMap((target) =>
    target.categories.map((category) => ({
      key: `${target.target_kind}:${target.target_id}:${category}`,
      target,
      category,
    })),
  );
  const choice = choices.find((item) => item.key === selection) ?? choices[0];
  const composerOpen = composerExpanded ?? data?.requests.length === 0;

  useEffect(() => {
    if (reporting) document.getElementById('tribe-assistance-report-reason')?.focus();
    else if (blocking) document.getElementById('tribe-assistance-block-title')?.focus();
    else if (withdrawing) document.getElementById('tribe-assistance-withdraw-title')?.focus();
  }, [reporting, blocking, withdrawing]);

  const submit = (action: AssistanceCommand) => {
    if (!available || locked || safetyLocked || !online || command.isPending || inFlight.current)
      return;
    inFlight.current = true;
    pending.current = action;
    setNotice('');
    command.mutate(action, {
      onSettled: () => {
        inFlight.current = false;
      },
      onSuccess: () => {
        pending.current = null;
        setReporting(undefined);
        setBlocking(undefined);
        setWithdrawing(undefined);
        if (action.type === 'create' || action.type === 'withdraw') {
          if (action.type === 'create') setComposerExpanded(false);
          requestAnimationFrame(() =>
            document.getElementById('tribe-assistance-requests-title')?.focus(),
          );
        }
        setNotice(
          action.type === 'help'
            ? 'Your offer to help is recorded. Complete the requested gameplay next.'
            : action.type === 'create'
              ? 'Your request is shared with the Tribe.'
              : action.type === 'withdraw'
                ? 'Your offer to help has been withdrawn. Your gameplay progress is unchanged.'
                : action.type === 'thanks'
                  ? 'Your thanks have been recorded.'
                  : action.type === 'report'
                    ? 'Your report has been sent. This request is hidden for you.'
                    : action.type === 'block'
                      ? 'This member is blocked from your assistance and social messages.'
                      : action.type === 'unblock'
                        ? 'The assistance block has been removed.'
                        : 'Your request has been cancelled.',
        );
      },
    });
  };
  const actionId = (type: AssistanceCommand['type']) => commandId(`assistance.${type}.${tribeId}`);

  const composer = data?.can_create && choice && (
    <section className="tribe-assistance__new-request" aria-label="New request">
      <SecondaryActionButton
        aria-expanded={composerOpen}
        aria-controls="tribe-assistance-compose"
        disabled={disabled && !composerOpen}
        onClick={() => setComposerExpanded(!composerOpen)}
      >
        {composerOpen ? 'Hide new request' : 'New help request'}
      </SecondaryActionButton>
      <div id="tribe-assistance-compose" hidden={!composerOpen}>
        {composerOpen && (
          <StonePanel className="tribe-assistance__compose">
            <h3>Ask for help</h3>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (disabled) return;
                submit({
                  type: 'create',
                  commandId: actionId('create'),
                  targetKind: choice.target.target_kind,
                  targetId: choice.target.target_id,
                  category: choice.category,
                });
              }}
            >
              <label htmlFor="tribe-assistance-goal">Goal</label>
              <select
                id="tribe-assistance-goal"
                value={`${choice.target.target_kind}:${choice.target.target_id}`}
                disabled={disabled}
                onChange={(event) => {
                  const next = choices.find(
                    (item) =>
                      `${item.target.target_kind}:${item.target.target_id}` === event.target.value,
                  );
                  if (next) setSelection(next.key);
                }}
              >
                {data.targets.map((target) => (
                  <option
                    key={`${target.target_kind}:${target.target_id}`}
                    value={`${target.target_kind}:${target.target_id}`}
                  >
                    {target.title}
                  </option>
                ))}
              </select>
              <label htmlFor="tribe-assistance-task">Request</label>
              <select
                id="tribe-assistance-task"
                value={choice.key}
                disabled={disabled}
                onChange={(event) => setSelection(event.target.value)}
              >
                {choices
                  .filter(
                    (item) =>
                      item.target.target_kind === choice.target.target_kind &&
                      item.target.target_id === choice.target.target_id,
                  )
                  .map((item) => (
                    <option key={item.key} value={item.key}>
                      {categories[item.category]}
                    </option>
                  ))}
              </select>
              <PrimaryActionButton type="submit" disabled={disabled}>
                Ask for help
              </PrimaryActionButton>
            </form>
          </StonePanel>
        )}
      </div>
    </section>
  );

  if (!enabled) return null;
  return (
    <section className="tribe-assistance-entry" aria-label="Tribe assistance">
      <div>
        <strong>Many hands, one shared city</strong>
        <span>Ask for supplies or help restore a missing expedition path.</span>
      </div>
      <SecondaryActionButton
        onClick={() => {
          setComposerExpanded(undefined);
          setOpen(true);
        }}
      >
        Help your Tribe
      </SecondaryActionButton>
      {createPortal(
        <div className="tribe-assistance-overlay">
          <Modal open={open} title="Help your Tribe" onClose={() => setOpen(false)}>
            <div className="tribe-assistance">
              <p className="tribe-assistance__intro">
                Choose a shared task. An offer to help is a first step; progress comes from playing
                together.
              </p>
              {safetyLocked && (
                <p role="status">
                  Stop safely before asking for help or responding. You can still read the board.
                </p>
              )}
              {!online && (
                <p role="status">You’re offline. Reconnect to check requests or respond.</p>
              )}
              {board.isPending && online && <p role="status">Checking your Tribe’s requests…</p>}
              {board.error && (
                <StatePanel
                  kind="error"
                  title="Requests are unavailable"
                  actionLabel="Try again"
                  onAction={() => void board.refetch()}
                >
                  We couldn’t confirm your current Tribe board. Reconnect or check your membership,
                  then try again.
                </StatePanel>
              )}
              {data && !data.enabled && <p role="status">Assistance is paused in this realm.</p>}
              {data?.enabled && (
                <>
                  {data.requests.length === 0 && composer}
                  {!choices.length && (
                    <p>
                      There is no open project requirement or missing expedition path to request.
                    </p>
                  )}
                  <section aria-labelledby="tribe-assistance-requests-title">
                    <h3 id="tribe-assistance-requests-title" tabIndex={-1}>
                      Shared requests
                    </h3>
                    {data.requests.length === 0 && (
                      <p>No requests to show. Your next shared task can begin here.</p>
                    )}
                    <ul className="tribe-assistance__requests">
                      {data.requests.map((request) => (
                        <li key={request.id}>
                          <StonePanel
                            className="tribe-assistance__request"
                            data-assistance-request={request.id}
                          >
                            <header>
                              <CategoryArt category={request.category} />
                              <div>
                                <h4>
                                  {categories[request.category]} · {request.title}
                                </h4>
                                <p>
                                  {request.is_own ? 'Your request' : request.owner_display_name} ·{' '}
                                  {statuses[request.status]}
                                </p>
                              </div>
                            </header>
                            {request.status === 'open' && (
                              <small>
                                Until{' '}
                                {new Date(request.expires_at).toLocaleString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </small>
                            )}
                            <div className="tribe-assistance__actions">
                              {request.can_help && (
                                <PrimaryActionButton
                                  disabled={disabled}
                                  onClick={() =>
                                    submit({
                                      type: 'help',
                                      requestId: request.id,
                                      commandId: actionId('help'),
                                    })
                                  }
                                >
                                  I can help
                                </PrimaryActionButton>
                              )}
                              {request.can_cancel && (
                                <SecondaryActionButton
                                  disabled={disabled}
                                  onClick={() =>
                                    submit({
                                      type: 'cancel',
                                      requestId: request.id,
                                      commandId: actionId('cancel'),
                                    })
                                  }
                                >
                                  Cancel request
                                </SecondaryActionButton>
                              )}
                              {request.status === 'open' &&
                                request.helpers.some((helper) => helper.is_self) && (
                                  <SecondaryActionButton
                                    disabled={disabled}
                                    onClick={() => {
                                      setOpen(false);
                                      // Let the modal restore its trigger focus before moving to the actual task.
                                      requestAnimationFrame(() => onPlay(request));
                                    }}
                                  >
                                    {request.target_kind === 'project'
                                      ? 'Open project contribution'
                                      : 'Open expedition paths'}
                                  </SecondaryActionButton>
                                )}
                            </div>
                            {request.helpers.length > 0 && (
                              <ul
                                className="tribe-assistance__helpers"
                                aria-label="Members offering help"
                              >
                                {request.helpers.map((helper) => (
                                  <li key={helper.member_id}>
                                    <span>
                                      {helper.is_self
                                        ? 'You offered to help'
                                        : `${helper.display_name} offered to help`}
                                    </span>
                                    {helper.thanked ? (
                                      <span>Thanked for contributing</span>
                                    ) : (
                                      helper.can_thank && (
                                        <SecondaryActionButton
                                          disabled={disabled}
                                          onClick={() =>
                                            submit({
                                              type: 'thanks',
                                              requestId: request.id,
                                              helperMemberId: helper.member_id,
                                              commandId: actionId('thanks'),
                                            })
                                          }
                                        >
                                          Thank {helper.display_name}
                                        </SecondaryActionButton>
                                      )
                                    )}
                                    {helper.can_withdraw && (
                                      <SecondaryActionButton
                                        disabled={disabled}
                                        onClick={() => {
                                          setReporting(undefined);
                                          setBlocking(undefined);
                                          setWithdrawing(request.id);
                                        }}
                                      >
                                        Withdraw offer
                                      </SecondaryActionButton>
                                    )}
                                    {!helper.is_self && (
                                      <SecondaryActionButton
                                        disabled={disabled}
                                        onClick={() => {
                                          setReporting(undefined);
                                          setWithdrawing(undefined);
                                          setBlocking({
                                            memberId: helper.member_id,
                                            name: helper.display_name,
                                          });
                                        }}
                                      >
                                        Block {helper.display_name}
                                      </SecondaryActionButton>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                            {!request.is_own && (
                              <div className="tribe-assistance__moderation">
                                {request.can_report && (
                                  <SecondaryActionButton
                                    disabled={disabled}
                                    onClick={() => {
                                      setReporting(request.id);
                                      setWithdrawing(undefined);
                                      setReason('spam');
                                      setBlocking(undefined);
                                    }}
                                  >
                                    Report request from {request.owner_display_name}
                                  </SecondaryActionButton>
                                )}
                                <SecondaryActionButton
                                  disabled={disabled}
                                  onClick={() => {
                                    setWithdrawing(undefined);
                                    setBlocking({
                                      memberId: request.owner_member_id,
                                      name: request.owner_display_name,
                                    });
                                    setReporting(undefined);
                                  }}
                                >
                                  Block {request.owner_display_name}
                                </SecondaryActionButton>
                              </div>
                            )}
                          </StonePanel>
                        </li>
                      ))}
                    </ul>
                  </section>
                  {data.requests.length > 0 && composer}
                  {withdrawing &&
                    data.requests.some(
                      (request) =>
                        request.id === withdrawing &&
                        request.helpers.some((helper) => helper.can_withdraw),
                    ) && (
                      <StonePanel className="tribe-assistance__confirmation">
                        <h3 id="tribe-assistance-withdraw-title" tabIndex={-1}>
                          Withdraw your offer to help?
                        </h3>
                        <p>
                          This removes your offer, not any contribution you already made. A later
                          offer starts fresh.
                        </p>
                        <div className="tribe-assistance__actions">
                          <PrimaryActionButton
                            disabled={disabled}
                            onClick={() =>
                              submit({
                                type: 'withdraw',
                                requestId: withdrawing,
                                commandId: actionId('withdraw'),
                              })
                            }
                          >
                            Confirm withdrawal
                          </PrimaryActionButton>
                          <SecondaryActionButton onClick={() => setWithdrawing(undefined)}>
                            Keep my offer
                          </SecondaryActionButton>
                        </div>
                      </StonePanel>
                    )}
                  {reporting &&
                    data.requests.some(
                      (request) => request.id === reporting && request.can_report,
                    ) && (
                      <StonePanel className="tribe-assistance__confirmation">
                        <h3>Report this request</h3>
                        <label htmlFor="tribe-assistance-report-reason">Reason</label>
                        <select
                          id="tribe-assistance-report-reason"
                          value={reason}
                          disabled={disabled}
                          onChange={(event) => setReason(event.target.value as typeof reason)}
                        >
                          {Object.entries(confirmations).map(([code, text]) => (
                            <option key={code} value={code}>
                              {text}
                            </option>
                          ))}
                        </select>
                        <p>This hides the request from your board and sends it for review.</p>
                        <div className="tribe-assistance__actions">
                          <PrimaryActionButton
                            disabled={disabled}
                            onClick={() =>
                              submit({
                                type: 'report',
                                requestId: reporting,
                                reason,
                                commandId: actionId('report'),
                              })
                            }
                          >
                            Send report
                          </PrimaryActionButton>
                          <SecondaryActionButton onClick={() => setReporting(undefined)}>
                            Go back
                          </SecondaryActionButton>
                        </div>
                      </StonePanel>
                    )}
                  {blocking && (
                    <StonePanel className="tribe-assistance__confirmation">
                      <h3 id="tribe-assistance-block-title" tabIndex={-1}>
                        Block {blocking.name}?
                      </h3>
                      <p>
                        You won’t see each other’s assistance requests, thanks or celebrations. You
                        can remove this block below. Existing gameplay and exchanges stay unchanged.
                      </p>
                      <div className="tribe-assistance__actions">
                        <PrimaryActionButton
                          disabled={disabled}
                          onClick={() =>
                            submit({
                              type: 'block',
                              memberId: blocking.memberId,
                              commandId: actionId('block'),
                            })
                          }
                        >
                          Confirm block
                        </PrimaryActionButton>
                        <SecondaryActionButton onClick={() => setBlocking(undefined)}>
                          Go back
                        </SecondaryActionButton>
                      </div>
                    </StonePanel>
                  )}
                  {data.blocks.length > 0 && (
                    <section aria-labelledby="tribe-assistance-blocks-title">
                      <h3 id="tribe-assistance-blocks-title">Your assistance blocks</h3>
                      <ul className="tribe-assistance__helpers">
                        {data.blocks.map((block) => (
                          <li key={block.id}>
                            <span>{block.display_name}</span>
                            <SecondaryActionButton
                              disabled={disabled}
                              onClick={() =>
                                submit({
                                  type: 'unblock',
                                  blockId: block.id,
                                  commandId: actionId('unblock'),
                                })
                              }
                            >
                              Unblock {block.display_name}
                            </SecondaryActionButton>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                  <p className="tribe-assistance__hint">
                    Thanks become available after a matching contribution is confirmed. Requests
                    never share a member’s location.
                  </p>
                  <TribeSocialPanel
                    tribeId={tribeId}
                    active={open}
                    locked={disabled}
                    onBlock={(memberId, name) => {
                      setReporting(undefined);
                      setWithdrawing(undefined);
                      setBlocking({ memberId, name });
                    }}
                  />
                </>
              )}
              {command.error && (
                <div className="inline-error" role="alert">
                  <p>{command.error.message}</p>
                  <div className="tribe-assistance__actions">
                    <SecondaryActionButton
                      disabled={
                        !available || locked || safetyLocked || !online || command.isPending
                      }
                      onClick={() => {
                        if (pending.current) submit(pending.current);
                      }}
                    >
                      Retry the same action
                    </SecondaryActionButton>
                    <SecondaryActionButton
                      disabled={!online || command.isPending}
                      onClick={() => {
                        void board.refetch().then((result) => {
                          if (result.isSuccess) {
                            command.reset();
                            pending.current = null;
                          }
                        });
                      }}
                    >
                      Check latest requests
                    </SecondaryActionButton>
                  </div>
                </div>
              )}
              {notice && <p role="status">{notice}</p>}
            </div>
            <footer className="tribe-assistance__footer">
              <SecondaryActionButton onClick={() => setOpen(false)}>
                Return to the city
              </SecondaryActionButton>
            </footer>
          </Modal>
        </div>,
        document.querySelector('.ar-game-shell') ?? document.body,
      )}
    </section>
  );
}
